import React, { useState } from "react";

/**
 * YouWo Design System Playground
 *
 * Comprehensive design system exploration with:
 * - Current vs Proposed color analysis
 * - Component examples (List items, Nav items, Buttons)
 * - Animation demonstrations
 * - Mobile vs Web styling
 * - Refactoring recommendations
 *
 * Based on Mobile GlobalStyles.ts analysis:
 * - Current themeBlue: #4ea4f5 (too bright, overused)
 * - Interactive states: interactiveHover #f3f3f5, interactivePressed #e5e5ea
 * - Text: textPrimary #000, textSecondary #5a5a5d, textHint #bebebf
 */

// ============================================================================
// DESIGN TOKENS - Current vs Proposed
// ============================================================================

const CURRENT_TOKENS = {
  primary: "#4ea4f5",      // themeBlue - overused
  secondary: "#8B5CF6",    // themePurple
  text: "#000",            // textPrimary
  textSecondary: "#5a5a5d",
  textHint: "#bebebf",
  border: "#c4c4c7",       // borderLight
  divider: "#e5e5e5",      // lineDivider
  hover: "#f3f3f5",        // interactiveHover
  pressed: "#e5e5ea",      // interactivePressed
  selected: "#ededf2",     // interactiveSelected
  background: "#f5f5f5",
  white: "#ffffff",
  cancel: "#e43b3b",       // cancelRed
};

const PROPOSED_TOKENS = {
  // Primary = Black (for main UI, buttons, text)
  primary: "#0F172A",      // Near-black slate
  primaryHover: "#1E293B",
  primaryLight: "#334155",

  // Highlight = Blue (sparingly - links, active states)
  highlight: "#3B82F6",
  highlightHover: "#2563EB",
  highlightSubtle: "#DBEAFE",
  highlightMuted: "#EFF6FF",

  // Text hierarchy
  text: "#0F172A",
  textSecondary: "#475569",
  textMuted: "#64748B",
  textHint: "#94A3B8",

  // Borders & Structure
  border: "#E2E8F0",
  borderStrong: "#CBD5E1",
  divider: "#F1F5F9",

  // Backgrounds
  background: "#F8FAFC",
  surface: "#FFFFFF",
  surfaceHover: "#F1F5F9",
  surfaceActive: "#E2E8F0",

  // Semantic
  success: "#22C55E",
  warning: "#F59E0B",
  error: "#EF4444",

  white: "#FFFFFF",
};

const DesignPlayground: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"colors" | "components" | "animations" | "recommendations">("colors");
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-[#0F172A]">YouWo Design System</h1>
              <p className="text-sm text-[#64748B]">Professional, minimal, content-focused</p>
            </div>
            <div className="flex gap-2">
              {(["colors", "components", "animations", "recommendations"] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab
                      ? "bg-[#0F172A] text-white"
                      : "text-[#475569] hover:bg-[#F1F5F9]"
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === "colors" && <ColorsSection />}
        {activeTab === "components" && <ComponentsSection hoveredItem={hoveredItem} setHoveredItem={setHoveredItem} />}
        {activeTab === "animations" && <AnimationsSection />}
        {activeTab === "recommendations" && <RecommendationsSection />}
      </main>
    </div>
  );
};

// ============================================================================
// COLORS SECTION
// ============================================================================

const ColorsSection: React.FC = () => (
  <div className="space-y-12">
    {/* Side by Side Comparison */}
    <section>
      <h2 className="text-xl font-semibold text-[#0F172A] mb-6">Current vs Proposed</h2>
      <div className="grid md:grid-cols-2 gap-8">
        {/* Current */}
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-6">
          <div className="flex items-center gap-2 mb-4">
            <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded font-medium">Current</span>
            <span className="text-sm text-[#64748B]">From Mobile GlobalStyles.ts</span>
          </div>
          <div className="space-y-3">
            <ColorRow color={CURRENT_TOKENS.primary} name="themeBlue" issue="Overused for everything" />
            <ColorRow color={CURRENT_TOKENS.text} name="textPrimary" />
            <ColorRow color={CURRENT_TOKENS.textSecondary} name="textSecondary" />
            <ColorRow color={CURRENT_TOKENS.border} name="borderLight" />
            <ColorRow color={CURRENT_TOKENS.hover} name="interactiveHover" />
            <ColorRow color={CURRENT_TOKENS.selected} name="interactiveSelected" />
          </div>
        </div>

        {/* Proposed */}
        <div className="bg-white rounded-xl border-2 border-green-500 p-6">
          <div className="flex items-center gap-2 mb-4">
            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded font-medium">Proposed</span>
            <span className="text-sm text-[#64748B]">Professional design system</span>
          </div>
          <div className="space-y-3">
            <ColorRow color={PROPOSED_TOKENS.primary} name="primary" note="Black for main UI" />
            <ColorRow color={PROPOSED_TOKENS.highlight} name="highlight" note="Blue sparingly" />
            <ColorRow color={PROPOSED_TOKENS.text} name="text" />
            <ColorRow color={PROPOSED_TOKENS.textMuted} name="textMuted" />
            <ColorRow color={PROPOSED_TOKENS.border} name="border" />
            <ColorRow color={PROPOSED_TOKENS.surfaceHover} name="surfaceHover" />
          </div>
        </div>
      </div>
    </section>

    {/* Full Proposed Palette */}
    <section>
      <h2 className="text-xl font-semibold text-[#0F172A] mb-6">Proposed Full Palette</h2>
      <div className="grid md:grid-cols-4 gap-6">
        {/* Primary Scale */}
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-5">
          <h3 className="text-sm font-semibold text-[#0F172A] mb-4">Primary (Black)</h3>
          <div className="space-y-2">
            <ColorSwatch color="#0F172A" name="900" label="Buttons, text" />
            <ColorSwatch color="#1E293B" name="800" label="Hover" />
            <ColorSwatch color="#334155" name="700" label="Icons" />
            <ColorSwatch color="#475569" name="600" label="Secondary" />
          </div>
        </div>

        {/* Highlight Scale */}
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-5">
          <h3 className="text-sm font-semibold text-[#0F172A] mb-4">Highlight (Blue)</h3>
          <div className="space-y-2">
            <ColorSwatch color="#3B82F6" name="500" label="Links, active" />
            <ColorSwatch color="#2563EB" name="600" label="Hover" />
            <ColorSwatch color="#DBEAFE" name="100" label="Selection BG" />
            <ColorSwatch color="#EFF6FF" name="50" label="Subtle BG" />
          </div>
        </div>

        {/* Neutral Scale */}
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-5">
          <h3 className="text-sm font-semibold text-[#0F172A] mb-4">Neutral (Gray)</h3>
          <div className="space-y-2">
            <ColorSwatch color="#64748B" name="500" label="Muted text" />
            <ColorSwatch color="#94A3B8" name="400" label="Placeholder" />
            <ColorSwatch color="#CBD5E1" name="300" label="Strong border" />
            <ColorSwatch color="#E2E8F0" name="200" label="Border" />
          </div>
        </div>

        {/* Surfaces */}
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-5">
          <h3 className="text-sm font-semibold text-[#0F172A] mb-4">Surfaces</h3>
          <div className="space-y-2">
            <ColorSwatch color="#FFFFFF" name="white" label="Cards" border />
            <ColorSwatch color="#F8FAFC" name="slate-50" label="Page BG" border />
            <ColorSwatch color="#F1F5F9" name="slate-100" label="Hover" border />
            <ColorSwatch color="#E2E8F0" name="slate-200" label="Active" border />
          </div>
        </div>
      </div>
    </section>

    {/* Design Principle */}
    <section className="bg-gradient-to-r from-[#0F172A] to-[#1E293B] rounded-xl p-8 text-white">
      <h2 className="text-xl font-semibold mb-4">The Principle: Competitor Analysis</h2>
      <p className="text-[#94A3B8] mb-6">Note-taking apps like Notion, Obsidian, Linear use black as primary with blue as sparse highlight</p>
      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-white/10 rounded-lg p-4">
          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center mb-3">
            <span className="text-[#0F172A] text-lg font-bold">1</span>
          </div>
          <h3 className="font-medium mb-1">Black = Primary</h3>
          <p className="text-sm text-[#94A3B8]">Main actions, text, buttons. Professional and content-focused.</p>
        </div>
        <div className="bg-white/10 rounded-lg p-4">
          <div className="w-10 h-10 bg-[#3B82F6] rounded-lg flex items-center justify-center mb-3">
            <span className="text-white text-lg font-bold">2</span>
          </div>
          <h3 className="font-medium mb-1">Blue = Highlight</h3>
          <p className="text-sm text-[#94A3B8]">Links, active states, selections. Used sparingly to draw attention.</p>
        </div>
        <div className="bg-white/10 rounded-lg p-4">
          <div className="w-10 h-10 bg-[#64748B] rounded-lg flex items-center justify-center mb-3">
            <span className="text-white text-lg font-bold">3</span>
          </div>
          <h3 className="font-medium mb-1">Gray = Structure</h3>
          <p className="text-sm text-[#94A3B8]">Borders, dividers, secondary text. Provides structure without distraction.</p>
        </div>
      </div>
    </section>
  </div>
);

// ============================================================================
// COMPONENTS SECTION
// ============================================================================

const ComponentsSection: React.FC<{
  hoveredItem: string | null;
  setHoveredItem: (item: string | null) => void;
}> = ({ hoveredItem, setHoveredItem }) => {
  const [selectedNote, setSelectedNote] = useState<number | null>(2);

  return (
    <div className="space-y-12">
      {/* Buttons */}
      <section>
        <h2 className="text-xl font-semibold text-[#0F172A] mb-2">Buttons</h2>
        <p className="text-[#64748B] mb-6">Mobile: DefaultButtonItem | Web: Standard button with hover states</p>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Current */}
          <div className="bg-white rounded-xl border border-red-200 p-6">
            <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded font-medium mb-4 inline-block">Current (Too Much Blue)</span>
            <div className="flex flex-wrap gap-3">
              <button className="px-5 py-2.5 bg-[#4ea4f5] text-white rounded-lg font-medium">Create Note</button>
              <button className="px-5 py-2.5 bg-[#4ea4f5] text-white rounded-lg font-medium">Export</button>
              <button className="px-5 py-2.5 bg-[#4ea4f5] text-white rounded-lg font-medium">Share</button>
            </div>
            <p className="text-xs text-red-600 mt-3">❌ Everything is blue - no hierarchy</p>
          </div>

          {/* Proposed */}
          <div className="bg-white rounded-xl border border-green-200 p-6">
            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded font-medium mb-4 inline-block">Proposed (Black Primary)</span>
            <div className="flex flex-wrap gap-3">
              <button className="px-5 py-2.5 bg-[#0F172A] text-white rounded-lg font-medium hover:bg-[#1E293B] transition-colors">
                Create Note
              </button>
              <button className="px-5 py-2.5 border border-[#CBD5E1] text-[#334155] rounded-lg font-medium hover:bg-[#F8FAFC] transition-colors">
                Export
              </button>
              <button className="px-5 py-2.5 text-[#3B82F6] rounded-lg font-medium hover:bg-[#EFF6FF] transition-colors">
                Share
              </button>
            </div>
            <p className="text-xs text-green-600 mt-3">✓ Clear hierarchy - primary action stands out</p>
          </div>
        </div>
      </section>

      {/* List Items - TypeListContent */}
      <section>
        <h2 className="text-xl font-semibold text-[#0F172A] mb-2">List Items (TypeListContent)</h2>
        <p className="text-[#64748B] mb-6">TypeListItem → TypeListItemContainer → TypeListView</p>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Current */}
          <div className="bg-white rounded-xl border border-red-200 overflow-hidden">
            <div className="px-4 py-3 bg-red-50 border-b border-red-200">
              <span className="text-xs font-medium text-red-700">Current Implementation</span>
            </div>
            <div className="divide-y divide-gray-200">
              {[
                { id: 1, title: "Meeting Notes", type: "Recording", time: "2h ago" },
                { id: 2, title: "Research Paper", type: "PDF", time: "Yesterday" },
                { id: 3, title: "YouTube Summary", type: "Video", time: "3 days ago" },
              ].map((note) => (
                <div
                  key={note.id}
                  className={`px-4 py-3 cursor-pointer transition-colors ${
                    selectedNote === note.id ? "bg-[#b6d7fe]" : "hover:bg-[#f3f3f5]"
                  }`}
                  onClick={() => setSelectedNote(note.id)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[#4ea4f5] rounded-lg flex items-center justify-center">
                      <span className="text-white text-xs">📝</span>
                    </div>
                    <div>
                      <p className="font-medium text-[#000]">{note.title}</p>
                      <p className="text-xs text-[#5a5a5d]">{note.type} · {note.time}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-4 py-2 bg-red-50 text-xs text-red-600">
              ❌ Blue icons, blue selection - inconsistent
            </div>
          </div>

          {/* Proposed */}
          <div className="bg-white rounded-xl border border-green-200 overflow-hidden">
            <div className="px-4 py-3 bg-green-50 border-b border-green-200">
              <span className="text-xs font-medium text-green-700">Proposed Implementation</span>
            </div>
            <div className="divide-y divide-[#E2E8F0]">
              {[
                { id: 1, title: "Meeting Notes", type: "Recording", time: "2h ago", icon: "🎙️" },
                { id: 2, title: "Research Paper", type: "PDF", time: "Yesterday", icon: "📄" },
                { id: 3, title: "YouTube Summary", type: "Video", time: "3 days ago", icon: "🎬" },
              ].map((note) => (
                <div
                  key={note.id}
                  onMouseEnter={() => setHoveredItem(`note-${note.id}`)}
                  onMouseLeave={() => setHoveredItem(null)}
                  className={`px-4 py-3 cursor-pointer transition-all ${
                    selectedNote === note.id
                      ? "bg-[#EFF6FF] border-l-2 border-[#3B82F6]"
                      : hoveredItem === `note-${note.id}`
                        ? "bg-[#F1F5F9]"
                        : ""
                  }`}
                  onClick={() => setSelectedNote(note.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        selectedNote === note.id ? "bg-[#DBEAFE]" : "bg-[#F1F5F9]"
                      }`}>
                        <span className="text-sm">{note.icon}</span>
                      </div>
                      <div>
                        <p className={`font-medium ${selectedNote === note.id ? "text-[#0F172A]" : "text-[#1E293B]"}`}>
                          {note.title}
                        </p>
                        <p className={`text-xs ${selectedNote === note.id ? "text-[#3B82F6]" : "text-[#64748B]"}`}>
                          {note.type} · {note.time}
                        </p>
                      </div>
                    </div>
                    {hoveredItem === `note-${note.id}` && selectedNote !== note.id && (
                      <button className="text-[#94A3B8] hover:text-[#64748B]">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="px-4 py-2 bg-green-50 text-xs text-green-600">
              ✓ Gray icons, blue only on selection, subtle hover
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Items - TypeNavItem */}
      <section>
        <h2 className="text-xl font-semibold text-[#0F172A] mb-2">Navigation / Sidebar (TypeNavItem)</h2>
        <p className="text-[#64748B] mb-6">Web sidebar navigation with hover states</p>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Current */}
          <div className="bg-white rounded-xl border border-red-200 p-4">
            <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded font-medium mb-4 inline-block">Current</span>
            <div className="space-y-1">
              <div className="px-3 py-2 bg-[#b6d7fe] rounded-lg flex items-center gap-2">
                <span>📝</span>
                <span className="font-medium text-[#000]">All Notes</span>
              </div>
              <div className="px-3 py-2 hover:bg-[#f3f3f5] rounded-lg flex items-center gap-2 cursor-pointer">
                <span>📁</span>
                <span className="text-[#000]">Folders</span>
              </div>
              <div className="px-3 py-2 hover:bg-[#f3f3f5] rounded-lg flex items-center gap-2 cursor-pointer">
                <span>⭐</span>
                <span className="text-[#000]">Favorites</span>
              </div>
            </div>
          </div>

          {/* Proposed */}
          <div className="bg-white rounded-xl border border-green-200 p-4">
            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded font-medium mb-4 inline-block">Proposed</span>
            <div className="space-y-1">
              <div className="px-3 py-2 bg-[#F1F5F9] rounded-lg flex items-center gap-2 border-l-2 border-[#0F172A]">
                <span className="text-[#0F172A]">📝</span>
                <span className="font-medium text-[#0F172A]">All Notes</span>
              </div>
              <div className="px-3 py-2 hover:bg-[#F8FAFC] rounded-lg flex items-center gap-2 cursor-pointer transition-colors">
                <span className="text-[#64748B]">📁</span>
                <span className="text-[#475569]">Folders</span>
              </div>
              <div className="px-3 py-2 hover:bg-[#F8FAFC] rounded-lg flex items-center gap-2 cursor-pointer transition-colors">
                <span className="text-[#64748B]">⭐</span>
                <span className="text-[#475569]">Favorites</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cards */}
      <section>
        <h2 className="text-xl font-semibold text-[#0F172A] mb-2">Cards (Grid View)</h2>
        <p className="text-[#64748B] mb-6">For grid layout in TypeListContent with itemViewMode="grid"</p>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Default Card */}
          <div className="bg-white rounded-xl border border-[#E2E8F0] p-4 hover:border-[#CBD5E1] hover:shadow-sm transition-all cursor-pointer">
            <div className="w-full h-24 bg-[#F1F5F9] rounded-lg mb-3 flex items-center justify-center">
              <span className="text-2xl">📝</span>
            </div>
            <h3 className="font-medium text-[#0F172A] text-sm mb-1 truncate">Meeting Notes</h3>
            <p className="text-xs text-[#64748B]">2 hours ago</p>
          </div>

          {/* Hovered Card */}
          <div className="bg-white rounded-xl border border-[#CBD5E1] shadow-sm p-4 cursor-pointer">
            <div className="w-full h-24 bg-[#F1F5F9] rounded-lg mb-3 flex items-center justify-center">
              <span className="text-2xl">📄</span>
            </div>
            <h3 className="font-medium text-[#0F172A] text-sm mb-1 truncate">Research Paper</h3>
            <p className="text-xs text-[#64748B]">Yesterday</p>
            <span className="text-[8px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded mt-2 inline-block">HOVER STATE</span>
          </div>

          {/* Selected Card */}
          <div className="bg-white rounded-xl border-2 border-[#3B82F6] p-4 cursor-pointer shadow-sm">
            <div className="w-full h-24 bg-[#DBEAFE] rounded-lg mb-3 flex items-center justify-center relative">
              <span className="text-2xl">🎬</span>
              <div className="absolute top-2 right-2 w-5 h-5 bg-[#3B82F6] rounded-full flex items-center justify-center">
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h3 className="font-medium text-[#0F172A] text-sm mb-1 truncate">YouTube Summary</h3>
            <p className="text-xs text-[#3B82F6]">Selected</p>
          </div>

          {/* With Badge */}
          <div className="bg-white rounded-xl border border-[#E2E8F0] p-4 hover:border-[#CBD5E1] transition-all cursor-pointer">
            <div className="w-full h-24 bg-[#F1F5F9] rounded-lg mb-3 flex items-center justify-center relative">
              <span className="text-2xl">🎙️</span>
              <span className="absolute top-2 right-2 px-2 py-0.5 bg-[#22C55E] text-white text-[10px] rounded-full font-medium">NEW</span>
            </div>
            <h3 className="font-medium text-[#0F172A] text-sm mb-1 truncate">Audio Recording</h3>
            <p className="text-xs text-[#64748B]">Just now</p>
          </div>
        </div>
      </section>

      {/* Form Elements */}
      <section>
        <h2 className="text-xl font-semibold text-[#0F172A] mb-2">Form Elements</h2>
        <p className="text-[#64748B] mb-6">Inputs, search, dropdowns</p>

        <div className="bg-white rounded-xl border border-[#E2E8F0] p-6">
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">Default Input</label>
              <input
                type="text"
                placeholder="Enter note title..."
                className="w-full px-4 py-2.5 border border-[#CBD5E1] rounded-lg text-[#0F172A] placeholder-[#94A3B8] focus:outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-[#DBEAFE] transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">Search</label>
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search notes..."
                  className="w-full pl-10 pr-4 py-2.5 border border-[#CBD5E1] rounded-lg text-[#0F172A] placeholder-[#94A3B8] focus:outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-[#DBEAFE]"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">Select</label>
              <select className="w-full px-4 py-2.5 border border-[#CBD5E1] rounded-lg text-[#0F172A] focus:outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-[#DBEAFE] bg-white">
                <option>All Notes</option>
                <option>Recordings</option>
                <option>PDFs</option>
              </select>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// ============================================================================
// ANIMATIONS SECTION
// ============================================================================

const AnimationsSection: React.FC = () => {
  const [isPressed, setIsPressed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  return (
    <div className="space-y-12">
      <section>
        <h2 className="text-xl font-semibold text-[#0F172A] mb-2">Button States & Transitions</h2>
        <p className="text-[#64748B] mb-6">Hover, Active, Loading states with smooth transitions</p>

        <div className="bg-white rounded-xl border border-[#E2E8F0] p-6">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Hover Demo */}
            <div className="text-center">
              <button className="px-6 py-2.5 bg-[#0F172A] text-white rounded-lg font-medium hover:bg-[#1E293B] active:scale-95 transition-all duration-150">
                Hover Me
              </button>
              <p className="text-xs text-[#64748B] mt-2">Darken on hover</p>
            </div>

            {/* Active/Press Demo */}
            <div className="text-center">
              <button
                onMouseDown={() => setIsPressed(true)}
                onMouseUp={() => setIsPressed(false)}
                onMouseLeave={() => setIsPressed(false)}
                className={`px-6 py-2.5 bg-[#0F172A] text-white rounded-lg font-medium transition-all duration-100 ${
                  isPressed ? "scale-95 bg-[#334155]" : "hover:bg-[#1E293B]"
                }`}
              >
                Press Me
              </button>
              <p className="text-xs text-[#64748B] mt-2">Scale down on press</p>
            </div>

            {/* Loading Demo */}
            <div className="text-center">
              <button
                onClick={() => { setIsLoading(true); setTimeout(() => setIsLoading(false), 2000); }}
                disabled={isLoading}
                className={`px-6 py-2.5 bg-[#0F172A] text-white rounded-lg font-medium transition-all ${
                  isLoading ? "opacity-70 cursor-not-allowed" : "hover:bg-[#1E293B]"
                }`}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Loading...
                  </span>
                ) : "Click to Load"}
              </button>
              <p className="text-xs text-[#64748B] mt-2">Loading spinner</p>
            </div>

            {/* Focus Ring */}
            <div className="text-center">
              <button className="px-6 py-2.5 bg-[#0F172A] text-white rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:ring-offset-2 transition-all">
                Tab to Focus
              </button>
              <p className="text-xs text-[#64748B] mt-2">Focus ring for a11y</p>
            </div>
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0F172A] mb-2">List Item Hover</h2>
        <p className="text-[#64748B] mb-6">Smooth background transition with options reveal</p>

        <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden max-w-md">
          {["Note 1", "Note 2", "Note 3"].map((note, i) => (
            <div
              key={i}
              className="group px-4 py-3 border-b border-[#E2E8F0] last:border-b-0 cursor-pointer hover:bg-[#F8FAFC] transition-colors duration-150"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-[#F1F5F9] rounded-lg flex items-center justify-center group-hover:bg-[#E2E8F0] transition-colors">
                    📝
                  </div>
                  <span className="text-[#0F172A]">{note}</span>
                </div>
                <button className="opacity-0 group-hover:opacity-100 text-[#94A3B8] hover:text-[#64748B] transition-opacity duration-150">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-[#0F172A] mb-2">Selection Animation</h2>
        <p className="text-[#64748B] mb-6">Border and background transition on select</p>

        <div className="flex gap-4">
          {[false, true].map((selected, i) => (
            <div
              key={i}
              className={`p-4 rounded-xl cursor-pointer transition-all duration-200 ${
                selected
                  ? "bg-[#EFF6FF] border-2 border-[#3B82F6] shadow-sm"
                  : "bg-white border border-[#E2E8F0] hover:border-[#CBD5E1]"
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                  selected ? "bg-[#DBEAFE]" : "bg-[#F1F5F9]"
                }`}>
                  📝
                </div>
                <div>
                  <p className="font-medium text-[#0F172A]">Note Item</p>
                  <p className={`text-xs transition-colors ${selected ? "text-[#3B82F6]" : "text-[#64748B]"}`}>
                    {selected ? "Selected" : "Not selected"}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

// ============================================================================
// RECOMMENDATIONS SECTION
// ============================================================================

const RecommendationsSection: React.FC = () => (
  <div className="space-y-8">
    <section className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
      <div className="px-6 py-4 bg-[#0F172A] text-white">
        <h2 className="text-lg font-semibold">Refactoring Plan: Mobile GlobalStyles.ts</h2>
      </div>
      <div className="p-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-medium text-[#0F172A] mb-3">Current Issues</h3>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-red-500">❌</span>
                <span><code className="bg-[#F1F5F9] px-1 rounded">themeBlue #4ea4f5</code> used everywhere</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500">❌</span>
                <span><code className="bg-[#F1F5F9] px-1 rounded">selectedBlue #b6d7fe</code> too saturated</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500">❌</span>
                <span>No semantic naming (what's <code className="bg-[#F1F5F9] px-1 rounded">gray700</code>?)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500">❌</span>
                <span>Inconsistent interactive states</span>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="font-medium text-[#0F172A] mb-3">Proposed Changes</h3>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                <span>Add <code className="bg-[#F1F5F9] px-1 rounded">primary: #0F172A</code> for main UI</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                <span>Rename <code className="bg-[#F1F5F9] px-1 rounded">themeBlue → highlight</code></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                <span>Add semantic tokens: <code className="bg-[#F1F5F9] px-1 rounded">textPrimary, textMuted, border</code></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                <span>Update selection to subtle blue <code className="bg-[#F1F5F9] px-1 rounded">#EFF6FF</code></span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
      <div className="px-6 py-4 bg-[#1E293B] text-white">
        <h2 className="text-lg font-semibold">Component Migration Checklist</h2>
      </div>
      <div className="p-6">
        <div className="space-y-4">
          {[
            { component: "TypeListContent", status: "high", changes: "Selection color, icon backgrounds, hover states" },
            { component: "TypeListItem", status: "high", changes: "Remove blue icons, use gray, blue only on active" },
            { component: "TypeNavItem", status: "high", changes: "Active indicator from blue bg to left border" },
            { component: "DefaultButtonItem", status: "medium", changes: "Primary button black, secondary outlined" },
            { component: "WebSidebar", status: "medium", changes: "Navigation active states, hover colors" },
            { component: "DynamicNav", status: "low", changes: "Header styling consistency" },
          ].map((item, i) => (
            <div key={i} className="flex items-center justify-between py-3 border-b border-[#E2E8F0] last:border-b-0">
              <div className="flex items-center gap-3">
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  item.status === "high" ? "bg-red-100 text-red-700" :
                  item.status === "medium" ? "bg-amber-100 text-amber-700" :
                  "bg-green-100 text-green-700"
                }`}>
                  {item.status.toUpperCase()}
                </span>
                <span className="font-medium text-[#0F172A]">{item.component}</span>
              </div>
              <span className="text-sm text-[#64748B]">{item.changes}</span>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
      <div className="px-6 py-4 bg-[#334155] text-white">
        <h2 className="text-lg font-semibold">Proposed GlobalStyles.ts Changes</h2>
      </div>
      <div className="p-6">
        <pre className="bg-[#0F172A] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm">
{`// Add to GlobalStyles.ts

export const Color = {
  // Primary = Black/Slate (main UI)
  primary: "#0F172A",
  primaryHover: "#1E293B",
  primaryLight: "#334155",

  // Highlight = Blue (sparingly!)
  highlight: "#3B82F6",       // Was: themeBlue
  highlightHover: "#2563EB",
  highlightSubtle: "#DBEAFE", // Was: selectedBlue
  highlightMuted: "#EFF6FF",

  // Text hierarchy
  textPrimary: "#0F172A",     // Was: #000
  textSecondary: "#475569",   // Was: #5a5a5d
  textMuted: "#64748B",
  textHint: "#94A3B8",        // Was: #bebebf

  // Borders & Structure
  border: "#E2E8F0",
  borderStrong: "#CBD5E1",    // Was: borderLight #c4c4c7

  // Surfaces
  surface: "#FFFFFF",
  surfaceHover: "#F8FAFC",    // Was: interactiveHover
  surfaceActive: "#F1F5F9",   // Was: interactivePressed

  // Keep existing semantic colors
  success: "#22C55E",
  warning: "#F59E0B",
  error: "#EF4444",
  cancelRed: "#e43b3b",       // Keep for backwards compat

  // Deprecated - map to new tokens
  themeBlue: "#3B82F6",       // → Use highlight
  selectedBlue: "#EFF6FF",    // → Use highlightMuted
  interactiveHover: "#F8FAFC", // → Use surfaceHover
};`}
        </pre>
      </div>
    </section>
  </div>
);

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

const ColorRow: React.FC<{ color: string; name: string; issue?: string; note?: string }> = ({ color, name, issue, note }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg border border-[#E2E8F0]" style={{ backgroundColor: color }} />
      <div>
        <span className="text-sm font-mono text-[#334155]">{name}</span>
        {note && <span className="text-xs text-[#64748B] ml-2">({note})</span>}
      </div>
    </div>
    {issue && <span className="text-xs text-red-500">{issue}</span>}
    <span className="text-xs font-mono text-[#94A3B8]">{color}</span>
  </div>
);

const ColorSwatch: React.FC<{ color: string; name: string; label: string; border?: boolean }> = ({ color, name, label, border }) => (
  <div className="flex items-center gap-2">
    <div
      className={`w-8 h-8 rounded-lg ${border ? "border border-[#E2E8F0]" : ""}`}
      style={{ backgroundColor: color }}
    />
    <div>
      <p className="text-xs font-mono text-[#334155]">{name}</p>
      <p className="text-[10px] text-[#94A3B8]">{label}</p>
    </div>
  </div>
);

export default DesignPlayground;
